<template>
  <!-- No mostrar en edit si field.hide; no mostrar en view si field.hidedisplay / field.hideDisplay -->
  <div v-if="isVisible" class="field-reference position-relative">
    <!-- Label del campo (si showLabel es true) -->
    <small
      v-if="showLabel && field.label"
      :for="computedFieldId"
      class="form-label w-100"
    >
      <strong>
        {{ $t(field.label) }}
      </strong>
      <FieldHistoryLabelButton :field="field" :mode="mode" />
    </small>
    <br v-if="showLabel && field.label" />

    <!-- MODO FILTRO -->
    <FilterFieldReference
      v-if="mode === 'edit' && isFilter"
      :field="field"
      :value="value"
      :fieldId="computedFieldId"
      :filterOperator="filterOperator"
      :referenceOptions="referenceOptions"
      @input="$emit('input', $event)"
      @filter-operator-change="$emit('filter-operator-change', $event)"
    />

    <!-- MODO EDIT -->
    <EditFieldReference
      v-else-if="effectiveFieldMode === 'edit'"
      :field="field"
      :value="value"
      :fieldId="computedFieldId"
      :editingFields="editingFields"
      :referenceOptions="referenceOptions"
      :popup="popup"
      :inlineLabel="inlineLabel"
      :inlineLabelRequired="inlineLabelRequired"
      :disableLock="disableLock"
      :calculated="calculated"
      @input="$emit('input', $event)"
      @update:editingFields="$emit('update:editingFields', $event)"
      @open-reference-modal="handleOpenReferenceModal"
    />

    <!-- MODO VIEW -->
    <!-- Si es array con items: lista + botón "Crear" siempre cuando addbutton -->
    <template v-else-if="effectiveFieldMode === 'view' && field.isArray && Array.isArray(value) && value.length > 0">
      <div class="list-values">
        <ul>
          <li v-for="(refValue, index) in sortedArrayReferenceValues" :key="arrayReferenceItemKey(refValue, index)">
            <ViewFieldReference
              :field="field"
              :value="refValue"
              :item="item"
              :modelName="modelName"
              :isGroupModel="isGroupModel"
              :referenceOptions="referenceOptions"
              :hideAddButton="hideAddButton"
              @open-reference-modal="$emit('open-reference-modal', $event)"
            >
              <!-- Acciones por ítem que agrega quien usa el campo (p. ej. desligar una nota) -->
              <template v-if="$scopedSlots['item-actions']" #item-actions="slotProps">
                <slot name="item-actions" v-bind="slotProps" />
              </template>
            </ViewFieldReference>
          </li>
        </ul>
      </div>
      <!-- Con addbutton, el botón se muestra siempre (incluso si ya hay items), salvo hideAddButton -->
      <ViewFieldReference
        v-if="field.addbutton && !hideAddButton"
        :field="field"
        :value="null"
        :item="item"
        :modelName="modelName"
        :isGroupModel="isGroupModel"
        :referenceOptions="referenceOptions"
        :hideAddButton="hideAddButton"
        @open-reference-modal="$emit('open-reference-modal', $event)"
      />
    </template>
    <!-- Array vacío: solo botón o "No X yet" -->
    <template v-else-if="effectiveFieldMode === 'view' && field.isArray && (!value || value.length === 0)">
      <ViewFieldReference
        :field="field"
        :value="null"
        :item="item"
        :modelName="modelName"
        :isGroupModel="isGroupModel"
        :referenceOptions="referenceOptions"
        :hideAddButton="hideAddButton"
        @open-reference-modal="$emit('open-reference-modal', $event)"
      />
    </template>
    <!-- Valor simple (no array) -->
    <ViewFieldReference
      v-else-if="effectiveFieldMode === 'view'"
      :field="field"
      :value="value"
      :item="item"
      :modelName="modelName"
      :isGroupModel="isGroupModel"
      :referenceOptions="referenceOptions"
      :hideAddButton="hideAddButton"
      @open-reference-modal="$emit('open-reference-modal', $event)"
    >
      <!-- Acciones por ítem que agrega quien usa el campo (p. ej. desligar una nota) -->
      <template v-if="$scopedSlots['item-actions']" #item-actions="slotProps">
        <slot name="item-actions" v-bind="slotProps" />
      </template>
    </ViewFieldReference>
  </div>
</template>

<script>
import EditFieldReference from "./reference/editField.vue";
import ViewFieldReference from "./reference/viewField.vue";
import FilterFieldReference from "./reference/filterField.vue";
import fieldEditableByMixin from "@/mixins/fieldEditableByMixin";
import FieldHistoryLabelButton from "../common/FieldHistoryLabelButton.vue";

export default {
  name: "FieldReference",
  mixins: [fieldEditableByMixin],
  components: {
    FieldHistoryLabelButton,
    EditFieldReference,
    ViewFieldReference,
    FilterFieldReference,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ["edit", "view"].includes(value),
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    item: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
    referenceOptions: {
      type: Array,
      default: () => [],
    },
    popup: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
    /** Label dibujado por el propio campo, al lado de lupa/agregar/editar. */
    inlineLabel: {
      type: String,
      default: "",
    },
    /** Marca el label propio como obligatorio (lo dibuja al costado). */
    inlineLabelRequired: {
      type: Boolean,
      default: false,
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
    isFilter: {
      type: Boolean,
      default: false,
    },
    filterOperator: {
      type: String,
      default: "=",
    },
    /** Si true, no mostrar el botón add en references (p. ej. en listado dynamic-model) */
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /** Orden alfabético por etiqueta visible (name/label/options) para reference isArray en vista */
    sortedArrayReferenceValues() {
      if (!Array.isArray(this.value) || this.value.length === 0) {
        return [];
      }
      const opts = this.referenceOptions || [];
      const getId = (refValue) => {
        if (refValue == null || refValue === "") return "";
        if (typeof refValue === "object" && refValue._id != null) {
          return String(refValue._id);
        }
        return String(refValue);
      };
      const labelForSort = (refValue) => {
        const idStr = getId(refValue);
        if (refValue && typeof refValue === "object" && refValue !== null) {
          let label = refValue.name || refValue.label || "";
          const fromOpts = opts.find((o) => o && String(o._id) === idStr);
          if (fromOpts && (fromOpts.name || fromOpts.label)) {
            label = fromOpts.name || fromOpts.label || label;
          }
          return String(label || idStr).trim() || idStr;
        }
        const fromOpts = opts.find((o) => o && String(o._id) === idStr);
        if (fromOpts) {
          return String(fromOpts.name || fromOpts.label || fromOpts._id || idStr).trim() || idStr;
        }
        return idStr;
      };
      return [...this.value].sort((a, b) =>
        labelForSort(a).localeCompare(labelForSort(b), undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );
    },
    computedFieldId() {
      return this.fieldId || `field-reference-${this.field.key}`;
    },
    /** Oculta en edit si field.hide; en view si field.hidedisplay o field.hideDisplay */
    isVisible() {
      if (this.mode === "edit") {
        return !this.field.hide;
      }
      if (this.mode === "view") {
        return !this.field.hidedisplay && !this.field.hideDisplay;
      }
      return true;
    },
  },
  mounted() {
    // Debug: verificar si el campo se está renderizando
    if (this.mode === 'view' && this.field.addbutton) {
      console.log('[FieldReference] Campo con addbutton detectado:', {
        fieldKey: this.field.key,
        fieldLabel: this.field.label,
        value: this.value,
        mode: this.mode,
        addbutton: this.field.addbutton
      });
    }
  },
  methods: {
    arrayReferenceItemKey(refValue, index) {
      if (refValue && typeof refValue === "object" && refValue._id != null) {
        return `ref-${String(refValue._id)}`;
      }
      if (refValue != null && refValue !== "") {
        return `ref-${String(refValue)}`;
      }
      return `ref-idx-${index}`;
    },
    handleOpenReferenceModal(field, item) {
      this.$emit("open-reference-modal", field, item);
    },
  },
};
</script>

<style scoped>
.field-reference {
  width: 100%;
}

.list-values {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.list-values ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  width: 100%;
}

.list-values li {
  display: block;
  width: 100%;
}
</style>
